import { makeStyles } from '@material-ui/core';

import { autocompleteStyles } from '@/components/shared/components/NewContextForms/makeStyleObjects/autocomplete.styles';
import { commonStyles } from '@/components/shared/components/NewContextForms/makeStyleObjects/common.styles';
import { dateStyles } from '@/components/shared/components/NewContextForms/makeStyleObjects/date.styles';
import { menuItemStyles } from '@/components/shared/components/NewContextForms/makeStyleObjects/menuItem.styles';
import { multiSelectStyles } from '@/components/shared/components/NewContextForms/makeStyleObjects/multiSelect.styles';
import { selectStyles } from '@/components/shared/components/NewContextForms/makeStyleObjects/select.styles';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';
import { mergeDeep } from '@/utils/mergeDeep';

interface UseCustomFieldStylesProps {
  fieldVariant?: FieldVariant;
  underlined?: boolean;
  disabled?: boolean;
  field?: boolean;
  highlightLabel?: boolean;
}

export const useCustomFieldStyles = ({
  underlined = true,
  fieldVariant = FieldVariant.dataRow,
  disabled,
  field,
  highlightLabel = false,
}: UseCustomFieldStylesProps) => {
  const { sm } = useScreen();
  const { typography, palette } = useCustomTheme();

  const fieldVariantIsForm = fieldVariant === FieldVariant.form;
  const borderBottomStyle = underlined ? `1px ${palette.grey[300]} solid !important` : 'none';
  const disabledColor = fieldVariantIsForm || field ? palette.grey[400] : palette.text.primary;

  const getMUIClasses = makeStyles((theme) =>
    commonStyles(theme, fieldVariant, borderBottomStyle, disabledColor, highlightLabel),
  );

  const getMUITextFieldClasses = makeStyles((theme) =>
    commonStyles(theme, fieldVariant, borderBottomStyle, disabledColor, highlightLabel),
  );

  const getMUIMenuItemClasses = makeStyles((theme) => menuItemStyles(theme, fieldVariant));

  const getMUISelectClasses = makeStyles((theme) =>
    mergeDeep(
      commonStyles(theme, fieldVariant, borderBottomStyle, disabledColor, highlightLabel),
      selectStyles(theme, fieldVariant),
    ),
  );

  const getMUIMultiselectClasses = makeStyles((theme) =>
    mergeDeep(
      commonStyles(theme, fieldVariant, borderBottomStyle, disabledColor, highlightLabel),
      multiSelectStyles(sm),
    ),
  );

  const getMUIAutocompleteFormClasses = makeStyles((theme) => autocompleteStyles(theme, fieldVariant));

  const getMUIDateClasses = makeStyles((theme) =>
    mergeDeep(
      commonStyles(theme, fieldVariant, borderBottomStyle, disabledColor, highlightLabel),
      dateStyles(fieldVariant),
    ),
  );

  const inputDateThemeParams = {
    overrides: {
      MuiInputAdornment: {
        positionEnd: {
          display: disabled && 'none',
          position: 'absolute',
          right: -1,
        },
      },
      MuiInput: {
        root: fieldVariantIsForm
          ? {}
          : {
              marginTop: 0,
              paddingTop: 0,
            },
      },
      MuiFormControl: {
        root: {
          margin: 0,
        },
      },
      MuiInputBase: {
        input: {
          width: 'auto',
          textAlignLast: fieldVariantIsForm ? 'left' : 'right',
          fontSize: typography.subtitle1.fontSize,
          color: palette.text.primary,
          fontWeight: 700,
          marginRight: fieldVariantIsForm || disabled ? 0 : 32,
        },
        root: {
          display: 'flex',
          justifyContent: 'space-between',
        },
      },
    },
  };

  return {
    getMUIClasses,
    getMUITextFieldClasses,
    getMUIMenuItemClasses,
    getMUISelectClasses,
    getMUIMultiselectClasses,
    getMUIAutocompleteFormClasses,
    getMUIDateClasses,
    inputDateThemeParams,
  };
};
